// Dialogs of the AI site editor: swapping a photo and adding a section.
import React, { useRef, useState } from "react";
import toast from "react-hot-toast";
import { ImageOff, Loader2, Search, Upload, X } from "lucide-react";
import { useCreateFileMutation } from "../../store/Reducer/file";
import { useLazySitePhotosQuery, useSiteSectionMutation } from "../../store/Reducer/aiApi";
import { newSectionId, outlineOf, READY_SECTIONS, sectionsOf, withSections } from "./siteModel";

const Shell = ({ title, onClose, children, wide }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
    <div role="dialog" aria-modal="true" aria-label={title} className={`max-h-[90vh] w-full overflow-y-auto rounded-xl bg-white shadow-xl ${wide ? "max-w-3xl" : "max-w-lg"}`} onClick={(e) => e.stopPropagation()}>
      <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-white px-5 py-3">
        <h3 className="font-semibold text-slate-800">{title}</h3>
        <button type="button" aria-label="Close" onClick={onClose} className="rounded p-1 text-gray-500 hover:bg-gray-100"><X size={18} /></button>
      </div>
      <div className="p-5">{children}</div>
    </div>
  </div>
);

// Phone photos are often 4000px+ and several MB. Anything wider than this is
// scaled down in the browser before upload, so the site stays fast.
const MAX_WIDTH = 2400;
const shrink = (file) => new Promise((resolve) => {
  if (!/^image\/(jpeg|png|webp)$/.test(file.type)) return resolve(file);
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    URL.revokeObjectURL(url);
    if (img.naturalWidth <= MAX_WIDTH) return resolve(file);
    const canvas = document.createElement("canvas");
    canvas.width = MAX_WIDTH;
    canvas.height = Math.round((img.naturalHeight * MAX_WIDTH) / img.naturalWidth);
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => resolve(blob ? new File([blob], file.name.replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" }) : file),
      "image/jpeg",
      0.85
    );
  };
  img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
  img.src = url;
});

export const ImageDialog = ({ current, currentAlt = "", onPick, onClose }) => {
  const [tab, setTab] = useState("upload");
  const [query, setQuery] = useState("");
  const [chosen, setChosen] = useState(null); // url of the new photo, before it is used
  const [alt, setAlt] = useState(currentAlt);
  const [dragging, setDragging] = useState(false);
  const [search, { data, isFetching, error }] = useLazySitePhotosQuery();
  const [upload, { isLoading: uploading }] = useCreateFileMutation();
  const fileRef = useRef(null);

  const onFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Choose an image file (JPG, PNG or WebP).");
    if (file.size > 8 * 1024 * 1024) return toast.error("That image is over 8 MB. Choose a smaller one.");
    try {
      const res = await upload(await shrink(file)).unwrap();
      const url = res?.data?.fileUrl;
      if (!url) throw new Error("no url");
      setChosen(url);
    } catch (err) {
      toast.error("The image couldn't be uploaded. Try again.");
    }
  };

  if (chosen) {
    return (
      <Shell title="Use this photo?" onClose={onClose}>
        <img src={chosen} alt="" className="mb-4 max-h-72 w-full rounded-lg object-cover" />
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-gray-600">Describe the photo (optional, helps people using screen readers and Google)</span>
          <input value={alt} onChange={(e) => setAlt(e.target.value)} maxLength={150} placeholder="e.g. Children in our Saturday Quran class"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-accent focus:outline-none" />
        </label>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={() => setChosen(null)} className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Choose another</button>
          <button type="button" onClick={() => onPick(chosen, alt.trim())} className="rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700">Use this photo</button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell title="Change photo" onClose={onClose} wide>
      {current && <img src={current} alt="Current" className="mb-4 h-32 w-full rounded-lg object-cover" />}
      <div role="tablist" className="mb-4 flex gap-1 border-b">
        {[["upload", "Upload your own"], ["photos", "Free photos"]].map(([key, label]) => (
          <button key={key} type="button" role="tab" aria-selected={tab === key} onClick={() => setTab(key)}
            className={`border-b-2 px-3 py-2 text-sm font-medium ${tab === key ? "border-accent text-accent" : "border-transparent text-gray-500"}`}>{label}</button>
        ))}
      </div>
      {tab === "upload" ? (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); onFile(e.dataTransfer.files?.[0]); }}
          className={`flex flex-col items-center gap-3 rounded-lg border-2 border-dashed p-8 text-center transition ${dragging ? "border-accent bg-accent/5" : "border-gray-300"}`}
        >
          <Upload className="text-gray-400" />
          <p className="text-sm text-gray-600">Drag a photo here, or choose one from your device</p>
          <p className="text-xs text-gray-400">JPG, PNG or WebP, up to 8 MB. Large photos are resized for you.</p>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" aria-label="Choose a photo" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
          <button type="button" disabled={uploading} onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-1.5 rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60">
            {uploading ? <><Loader2 size={15} className="animate-spin" /> Uploading…</> : "Choose photo"}
          </button>
        </div>
      ) : (
        <>
          <form onSubmit={(e) => { e.preventDefault(); if (query.trim().length > 1) search(query.trim()); }} className="flex gap-2">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-2.5 text-gray-400" />
              <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder="e.g. mosque interior, Quran, community"
                aria-label="Search photos" className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-accent focus:outline-none" />
            </div>
            <button type="submit" className="rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700">Search</button>
          </form>
          <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
            {isFetching && <p className="col-span-full flex items-center gap-2 text-sm text-gray-500"><Loader2 size={15} className="animate-spin" /> Searching…</p>}
            {!isFetching && error && <p className="col-span-full text-sm text-red-600">The search didn't work. Try again.</p>}
            {!isFetching && data?.photos?.length === 0 && <p className="col-span-full text-sm text-gray-500">No photos found. Try other words.</p>}
            {!isFetching && data?.photos?.map((url) => (
              <button key={url} type="button" onClick={() => setChosen(url)} className="overflow-hidden rounded-lg ring-accent hover:ring-2 focus:ring-2">
                <img src={url} alt="" loading="lazy" className="aspect-[4/3] w-full object-cover" />
              </button>
            ))}
          </div>
          <p className="mt-3 text-xs text-gray-400">Photos from Pexels, free to use.</p>
        </>
      )}
      {current && (
        <div className="mt-5 border-t pt-4">
          <button type="button" onClick={() => onPick("")} className="inline-flex items-center gap-1.5 text-sm font-medium text-red-700 hover:underline">
            <ImageOff size={15} /> Remove this photo
          </button>
        </div>
      )}
    </Shell>
  );
};

export const AddSectionDialog = ({ site, after, onAdd, onClose }) => {
  const [name, setName] = useState("");
  const [brief, setBrief] = useState("");
  const [buildSection, { isLoading }] = useSiteSectionMutation();

  const add = async () => {
    const title = name.trim() || "New section";
    if (brief.trim().length < 5) return toast.error("Describe what the section should show.");
    const id = newSectionId(site, title);
    try {
      const res = await buildSection({ outline: outlineOf(site), sectionId: id, name: title, brief: brief.trim() }).unwrap();
      const section = { id, name: title, html: res.html, prompt: brief.trim(), hidden: false };
      onAdd((current) => {
        const list = [...sectionsOf(current)];
        const at = after ? list.findIndex((s) => s.id === after) + 1 : list.length;
        list.splice(at > 0 ? at : list.length, 0, section);
        return withSections(current, list);
      }, id);
    } catch (err) {
      toast.error(err?.data?.error || "The AI couldn't build that section. Try again.");
    }
  };

  return (
    <Shell title="Add a section" onClose={isLoading ? () => {} : onClose}>
      <p className="mb-2 text-sm font-medium text-slate-700">Start from</p>
      <div className="mb-4 flex flex-wrap gap-1.5">
        {READY_SECTIONS.map(([label, text]) => (
          <button key={label} type="button" disabled={isLoading} onClick={() => { setName(label); setBrief(text); }}
            className={`rounded-full border px-3 py-1 text-xs ${name === label ? "border-accent bg-accent/10 text-accent" : "border-gray-300 text-gray-700 hover:bg-gray-50"}`}>{label}</button>
        ))}
      </div>
      <label className="mb-3 block">
        <span className="mb-1 block text-xs font-medium text-gray-600">Name</span>
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} disabled={isLoading} placeholder="e.g. Weekend school"
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-accent focus:outline-none" />
      </label>
      <label className="block">
        <span className="mb-1 block text-xs font-medium text-gray-600">What should it show?</span>
        <textarea value={brief} onChange={(e) => setBrief(e.target.value)} maxLength={600} rows={4} disabled={isLoading}
          placeholder="e.g. Our Saturday school for ages 5-14: Quran, Arabic and Islamic studies, with how to enrol."
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-accent focus:outline-none" />
      </label>
      <button type="button" onClick={add} disabled={isLoading}
        className="mt-4 inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60">
        {isLoading ? <><Loader2 size={15} className="animate-spin" /> Designing your section…</> : "Add section"}
      </button>
    </Shell>
  );
};
